import React, { useEffect, useRef, useState } from 'react';
import styles from '../Upload.module.scss';
import { getAssetTypeName } from '../../../data/assets/helpers';
import { AutoForm, AutoFormRef, FORM_DATATYPES, TpAutoFormData } from '../../../components/auto-form-modal/AutoForm';
import { UploadDragAndDrop } from './UploadDragAndDrop';
import { UploadFile, fileMatchesPattern } from '../../../components/files/helpers';
import { extractDropdownFields } from '../actions/dropdownSelectors';

interface NewDataUploadProps {
  ontology: any;
  selectedTemplate: any;
  fileSet: UploadFile[];
  setFileSet: (newValue: UploadFile[]) => void;
  collectionProperties: any;
  setCollectionProperties: (newValue: any) => void;
  setFilesSelected: (newValue: boolean) => void;
}

export function NewDataUpload(props: NewDataUploadProps) {
  const {
    ontology,
    selectedTemplate,
    fileSet,
    setFileSet,
    collectionProperties,
    setCollectionProperties,
    setFilesSelected,
  } = props;
  const requiredFormRef = useRef<AutoFormRef>(null);
  const optionalFormRef = useRef<AutoFormRef>(null);
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>(fileSet);
  const [requiredForm, setRequiredForm] = useState<TpAutoFormData | null>(null);
  const [optionalForm, setOptionalForm] = useState<TpAutoFormData | null>(null);
  const [dropdownFields, setDropdownFields] = useState<Record<string, Array<{ id: string; content: string }>>>({});

  const submitForm = () => {
    const validateRequiredData = requiredFormRef.current?.validateFormData() || {};
    const validateOptionalData = optionalFormRef.current?.validateFormData() || {};

    // Only proceed if required data is valid
    if (Object.keys(validateRequiredData).length > 0 || !requiredFormRef.current) {
      const combinedData = { ...validateRequiredData, ...validateOptionalData };
      setCollectionProperties(combinedData);
      setFileSet(selectedFiles);
      setFilesSelected(true);
    }
  };

  useEffect(() => {
    if (selectedTemplate?.collectionMetadata) {
      const dropdowns = extractDropdownFields(selectedTemplate.collectionMetadata);
      setDropdownFields(dropdowns);

      const requiredFields = selectedTemplate.collectionMetadata
        .filter((field: any) => field.required)
        .map((field: any) => {
          const baseField = {
            id: field.id,
            name: field.name,
            dataType: field.dataType,
            required: field.required,
            startContent: collectionProperties[field.id] || '',
          };
          if (field.dataType === FORM_DATATYPES.MULTIPLECHOICE && Array.isArray(field.choices)) {
            return {
              ...baseField,
              choices: field.choices,
            };
          }

          return baseField;
        });

      const optionalFields = selectedTemplate.collectionMetadata
        .filter((field: any) => !field.required)
        .map((field: any) => {
          const baseField = {
            id: field.id,
            name: field.name,
            dataType: field.dataType,
            required: field.required,
            startContent: collectionProperties[field.id] || '',
          };
          if (field.dataType === FORM_DATATYPES.MULTIPLECHOICE && Array.isArray(field.choices)) {
            return {
              ...baseField,
              choices: field.choices,
              disableTransparentBackground: true,
            };
          }

          return baseField;
        });

      setRequiredForm({
        autoFormDef: {
          title: '',
          fields: requiredFields,
          actionButtons: [],
          requiredAsterisk: true,
        },
      });

      setOptionalForm({
        autoFormDef: {
          title: '',
          fields: optionalFields,
          actionButtons: [],
          requiredAsterisk: true,
        },
      });
    }
  }, [selectedTemplate, collectionProperties]);

  if (requiredForm && optionalForm) {
    const hasOptionalFields = optionalForm.autoFormDef.fields.length > 0;

    return (
      <>
        <div className={styles.collectionAssetType}>
          <div className={styles.uploadSectionHeader}>
            {getAssetTypeName(ontology.assetTypes[selectedTemplate.collectionAssetType])}
          </div>
          {hasOptionalFields ? (
            <div className={styles.twoColumnLayout}>
              <div className={styles.requiredColumn}>
                <div className={styles.autoForm}>
                  <AutoForm ref={requiredFormRef} activeForm={requiredForm} />
                </div>
              </div>
              <div className={styles.columnDivider}></div>
              <div className={styles.optionalColumn}>
                <div className={styles.autoForm}>
                  <AutoForm ref={optionalFormRef} activeForm={optionalForm} />
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.singleColumnLayout}>
              <div className={styles.autoForm}>
                <AutoForm ref={requiredFormRef} activeForm={requiredForm} />
              </div>
            </div>
          )}
        </div>
        <div className={styles.fileSetAssetType}>
          <div className={styles.uploadSectionHeader}>
            {getAssetTypeName(ontology.assetTypes[selectedTemplate.fileSetAssetType], true)}
          </div>
          <UploadDragAndDrop
            selectedTemplate={selectedTemplate}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
          />
        </div>
        <div className={styles.buttonSection}>
          <button
            onClick={() => {
              submitForm();
            }}
            disabled={
              selectedFiles.length == 0 ||
              selectedFiles.some((f: UploadFile) => {
                return !fileMatchesPattern(f.name, selectedTemplate);
              })
            }
          >
            Next
          </button>
        </div>
      </>
    );
  }

  return null;
}
